import React, {
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { socketsClient } from '../../api/sockets';

import {
  Header,
  HeaderActions,
  ButtonDownload,
  Title,
  Table,
  TableRow,
  TableCell,
  SelectFilter,
  DateFilter,
  ButtonSimple,
  Button,
  Spinner,
} from '../../components';
import {
  formatMoney,
  mapCouponType,
  isFilterActive,
  formatDate,
  formatFilename,
  generateHeader,
  buildTableFilterBody,
} from '../../utils';

import styles from './coupons.module.scss';
import { Api } from '../../api/api';
import { config } from '../../api/config';
import { actions } from '../../state';
import { CouponsContext } from './coupons.context';
import { CouponTypes, isDiscountCouponType } from './coupon.data';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const getMessageIdentifiers = message =>
  [
    message?.id,
    message?.job_id,
    message?.coupon_id,
    message?.payload?.id,
    message?.payload?.job_id,
    message?.payload?.coupon_id,
    message?.payload?.data?.id,
    message?.payload?.data?.job_id,
    message?.payload?.data?.coupon_id,
  ]
    .filter(value => value !== undefined && value !== null)
    .map(String);

const getPendingCouponGeneratedAmount = coupon =>
  Number(coupon?.code_amount ?? coupon?.coupon_code_count ?? 0);

const isPendingCouponComplete = (coupon, pendingCouponCreation) =>
  !!coupon &&
  getPendingCouponGeneratedAmount(coupon) >=
    Number(pendingCouponCreation?.expectedCodeAmount || 0);

const Coupons = ({
  dispatch,
  coupons: {
    coupons,
    total,
    footer,
    header = {},
    loading,
    couponExport,
    pendingCouponCreation,
  },
  xlsLoading,
  user,
  history,
}) => {
  const {
    createdAtLtFilter,
    setCreatedAtLtFilter,
    createdAtGtFilter,
    setCreatedAtGtFilter,
    expiresAtLtFilter,
    setExpiresAtLtFilter,
    expiresAtGtFilter,
    setExpiresAtGtFilter,
    nameFilter,
    setNameFilter,
    typeFilter,
    setTypeFilter,
    sortOrder,
    setSortOrder,
    filterBody,
    setFilterBody,
  } = useContext(CouponsContext);
  const [page, setPage] = useState(1);
  const isRefreshingPendingCoupon = useRef(false);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  useEffect(() => {
    dispatch(actions.coupons.setExportIdentifierAction({}));
  }, [dispatch]);

  const fetchCoupons = useCallback(
    data => {
      dispatch(actions.coupons.getCouponsAction(data));
    },
    [dispatch]
  );

  const clearCoupons = useCallback(() => {
    dispatch(actions.coupons.clearCouponsAction());
  }, [dispatch]);

  useEffect(() => () => clearCoupons(), [clearCoupons]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchCoupons({ ...(filterBody || {}), offset });
  }, [fetchCoupons, clearCoupons, filterBody, page]);

  const pendingCoupon = useMemo(
    () =>
      pendingCouponCreation
        ? coupons.find(
            ({ id }) => Number(id) === Number(pendingCouponCreation.couponId)
          )
        : null,
    [coupons, pendingCouponCreation]
  );

  const refreshPendingCoupon = useCallback(async () => {
    if (!pendingCouponCreation || isRefreshingPendingCoupon.current) {
      return;
    }

    isRefreshingPendingCoupon.current = true;

    try {
      const coupon = await Api.getCoupon(pendingCouponCreation.couponId);

      if (!coupon) {
        return;
      }

      if (isPendingCouponComplete(coupon, pendingCouponCreation)) {
        dispatch(actions.coupons.clearPendingCouponCreationAction());
        history.push(
          config.APP_PATHS.couponDetails(pendingCouponCreation.couponId)
        );
        return;
      }

      dispatch(
        actions.coupons.setPendingCouponCreationAction({
          ...pendingCouponCreation,
          preview: {
            ...pendingCouponCreation.preview,
            ...coupon,
          },
        })
      );
    } catch (error) {
      // Keep showing the pending row until the coupon becomes available.
    } finally {
      isRefreshingPendingCoupon.current = false;
    }
  }, [dispatch, history, pendingCouponCreation]);

  useEffect(() => {
    if (!user?.id) {
      return undefined;
    }

    const channel = socketsClient.subscribe(`admin/${user.id}`);
    const handleMessage = message => {
      if (!pendingCouponCreation) {
        return;
      }

      const messageIdentifiers = getMessageIdentifiers(message);
      const pendingIdentifiers = [
        pendingCouponCreation.jobId,
        pendingCouponCreation.couponId,
      ]
        .filter(value => value !== undefined && value !== null)
        .map(String);

      if (
        pendingIdentifiers.some(identifier =>
          messageIdentifiers.includes(identifier)
        )
      ) {
        refreshPendingCoupon();
      }
    };

    channel.watch(handleMessage);

    return () => {
      if (typeof channel?.unwatch === 'function') {
        channel.unwatch(handleMessage);
      }
    };
  }, [
    couponExport,
    dispatch,
    pendingCouponCreation,
    refreshPendingCoupon,
    user,
  ]);

  const xlsExport = () => {
    const filename = formatFilename(config.PAGES.coupons);
    dispatch(actions.coupons.exportCouponsAsyncAction(filterBody, filename));
  };

  const handleSubmit = submitOptions => {
    const generatedFilter = [];

    if (nameFilter.length) {
      generatedFilter.push({ field: 'title', value: nameFilter });
    }

    if (typeFilter.length) {
      generatedFilter.push({ field: 'type', value: typeFilter });
    }

    if (createdAtGtFilter) {
      generatedFilter.push({
        field: 'valid_from',
        expression: 'gte',
        value: createdAtGtFilter,
        type: 'timestamp',
      });
    }
    if (createdAtLtFilter) {
      generatedFilter.push({
        field: 'valid_from',
        expression: 'lte',
        value: createdAtLtFilter,
        type: 'timestamp',
      });
    }

    if (expiresAtGtFilter) {
      generatedFilter.push({
        field: 'valid_until',
        expression: 'gte',
        value: expiresAtGtFilter,
        type: 'timestamp',
      });
    }
    if (expiresAtLtFilter) {
      generatedFilter.push({
        field: 'valid_until',
        expression: 'lte',
        value: expiresAtLtFilter,
        type: 'timestamp',
      });
    }

    setFilterBody(
      buildTableFilterBody({
        submitOptions,
        generatedFilter,
        sortOrder,
      })
    );
    setPage(1);
  };

  const clearFilters = () => {
    setTypeFilter([]);
    setNameFilter([]);
    setCreatedAtLtFilter();
    setCreatedAtGtFilter();
    setExpiresAtLtFilter();
    setExpiresAtGtFilter();

    handleSubmit({ clear: true });
    setPage(1);
  };

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const tableHeader = [
    <SelectFilter
      title="Pavadinimas"
      name="title"
      valueType="string"
      array={generateHeader(header.title)}
      filterState={nameFilter}
      setFilterState={setNameFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', nameFilter)}
    />,
    <SelectFilter
      title="Tipas"
      name="type"
      valueType="number"
      array={generateHeader(header.type, mapCouponType)}
      filterState={typeFilter}
      setFilterState={setTypeFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('select', typeFilter)}
    />,
    <div className={styles.tableHeader}>Suma / %</div>,
    <div className={styles.tableHeader}>Panaudoti</div>,
    <div className={styles.tableHeader}>Viso kuponų</div>,
    <DateFilter
      title="Galioja nuo"
      name="valid_from"
      lt={createdAtLtFilter}
      setLt={setCreatedAtLtFilter}
      gt={createdAtGtFilter}
      setGt={setCreatedAtGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', createdAtLtFilter, createdAtGtFilter)}
    />,
    <DateFilter
      title="Galioja iki"
      name="valid_until"
      lt={expiresAtLtFilter}
      setLt={setExpiresAtLtFilter}
      gt={expiresAtGtFilter}
      setGt={setExpiresAtGtFilter}
      onSubmit={handleSubmit}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      isActive={isFilterActive('date', expiresAtLtFilter, expiresAtGtFilter)}
    />,
  ];

  const tableFooter = ['', '', '', '', '', '', ''];

  const formatCouponAmount = (type, amount) => {
    if (type === CouponTypes.MEMBERSHIP) {
      return '-';
    }

    if (isDiscountCouponType(type)) {
      return `${amount || 0}%`;
    }

    return formatMoney(amount);
  };

  const renderPendingCellContent = content => (
    <span className={styles.pendingCouponCellContent}>{content}</span>
  );

  const formatPendingDate = value => (value ? formatDate(value) : '-');

  const renderPendingRow = () => {
    if (!pendingCouponCreation) {
      return null;
    }

    const pendingRow = pendingCoupon || pendingCouponCreation.preview;
    const currentCouponCodeCount = getPendingCouponGeneratedAmount(pendingRow);

    return (
      <TableRow
        key={`pending-coupon-${pendingCouponCreation.couponId}`}
        className={styles.pendingCouponRow}
      >
        <TableCell>
          {renderPendingCellContent(pendingRow?.title || 'Kuriamas kuponas')}
        </TableCell>
        <TableCell>
          {renderPendingCellContent(mapCouponType(pendingRow?.type))}
        </TableCell>
        <TableCell>
          {renderPendingCellContent(
            formatCouponAmount(pendingRow?.type, pendingRow?.amount)
          )}
        </TableCell>
        <TableCell align="center" verticalCenter>
          <div className={styles.pendingCouponLoader}>
            <Spinner />
          </div>
        </TableCell>
        <TableCell>
          {renderPendingCellContent(
            `${currentCouponCodeCount}/${pendingCouponCreation.expectedCodeAmount}`
          )}
        </TableCell>
        <TableCell>
          {renderPendingCellContent(formatPendingDate(pendingRow?.valid_from))}
        </TableCell>
        <TableCell>
          {renderPendingCellContent(formatPendingDate(pendingRow?.valid_until))}
        </TableCell>
      </TableRow>
    );
  };

  const displayedCoupons = pendingCouponCreation
    ? coupons.filter(
        ({ id }) => Number(id) !== Number(pendingCouponCreation.couponId)
      )
    : coupons;

  const renderTable = () =>
    displayedCoupons.map(
      ({
        id,
        title,
        valid_from,
        valid_until,
        type,
        amount,
        coupon_code_count,
        total_redeem_count,
      }) => (
        <TableRow url={config.APP_PATHS.couponDetails(id)} key={id}>
          <TableCell>{title}</TableCell>
          <TableCell>{mapCouponType(type)}</TableCell>
          <TableCell>{formatCouponAmount(type, amount)}</TableCell>
          <TableCell>{total_redeem_count}</TableCell>
          <TableCell>{coupon_code_count}</TableCell>
          <TableCell>{formatDate(valid_from)}</TableCell>
          <TableCell>{formatDate(valid_until)}</TableCell>
        </TableRow>
      )
    );

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.coupons}</Title>

        <HeaderActions>
          {filterBody && (
            <ButtonSimple type="filter" onClick={clearFilters}>
              Išvalyti filtrus
            </ButtonSimple>
          )}
          <Button color="primary" url={config.APP_PATHS.addCoupon}>
            Sukurti
          </Button>
          <ButtonDownload onClick={xlsExport} loading={xlsLoading} />
        </HeaderActions>
      </Header>

      <Table
        header={tableHeader}
        footer={tableFooter}
        isEmpty={!displayedCoupons.length && !pendingCouponCreation}
        filter={true}
        loading={loading}
        rightAlignedFooterCells={[3, 4]}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderPendingRow()}
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  coupons: state.coupons,
  xlsLoading: state.download.xlsLoading,
  user: state.auth.user,
});

Coupons.propTypes = {
  dispatch: PropTypes.func.isRequired,
  coupons: PropTypes.shape({
    coupons: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    header: PropTypes.shape({
      type: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    footer: PropTypes.shape({}),
    loading: PropTypes.bool.isRequired,
    couponExport: PropTypes.shape({
      id: PropTypes.string,
      filename: PropTypes.string,
    }).isRequired,
    pendingCouponCreation: PropTypes.shape({
      jobId: PropTypes.string,
      couponId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      expectedCodeAmount: PropTypes.number,
      preview: PropTypes.shape({
        title: PropTypes.string,
        type: PropTypes.number,
        amount: PropTypes.number,
        code_amount: PropTypes.number,
        coupon_code_count: PropTypes.number,
        valid_from: PropTypes.string,
        valid_until: PropTypes.string,
      }),
    }),
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(Coupons);
